import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lightbulb, AlertTriangle, ListChecks, Building2, RefreshCw, Target, FileText, ThumbsUp } from "lucide-react";

const LEVEL = {
  yuksek: { l: "Yüksek risk", cls: "bg-red-600" },
  orta: { l: "Orta risk", cls: "bg-amber-500" },
  dusuk: { l: "Düşük risk", cls: "bg-slate-500" },
};

export default function Strategy() {
  const stT = useTableSort([]);
  const [committee, setCommittee] = useState("all");
  const [branch, setBranch] = useState("all");
  const [branches, setBranches] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [rep, setRep] = useState(null);
  const [seg, setSeg] = useState("all");
  const [dirUsers, setDirUsers] = useState([]);
  const [assignee, setAssignee] = useState({});
  const [busy, setBusy] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const params = committee !== "all" ? { committee_no: Number(committee) } : {};
      if (branch !== "all") params.branch = branch;
      const res = await api.get("/reports/strategy/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `strateji_raporu_${committee === "all" ? "tum" : committee}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF oluşturulamadı");
    } finally { setPdfBusy(false); }
  };

  useEffect(() => {
    api.get("/committees").then(({ data }) => setCommittees(data || [])).catch(() => {});
    api.get("/dashboard/branch-scorecard").then(({ data }) => {
      const rows = Array.isArray(data) ? data : (data.rows || data.branches || []);
      setBranches(rows.map((r) => r.branch).filter((b) => b && b !== "Merkez"));
    }).catch(() => {});
    api.get("/messages/directory").then(({ data }) => {
      const seen = new Set(); const out = [];
      const push = (u) => { if (u && !u.self && !seen.has(u.id)) { seen.add(u.id); out.push(u); } };
      (data.general || []).forEach(push);
      (data.others || []).forEach(push);
      (data.committees || []).forEach((c) => (c.users || []).forEach(push));
      setDirUsers(out);
    }).catch(() => {});
  }, []);

  const load = () => {
    setRep(null);
    const params = committee !== "all" ? { committee_no: Number(committee) } : {};
    if (branch !== "all") params.branch = branch;
    api.get("/reports/strategy", { params }).then(({ data }) => setRep(data))
      .catch(() => { setRep(null); toast.error("Strateji üretilemedi"); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [committee, branch]);

  const firms = useMemo(
    () => (rep?.firms || []).filter((f) => seg === "all" || f.segment === seg),
    [rep, seg]
  );

  const assign = async (f) => {
    const assignee_id = assignee[f.id];
    if (!assignee_id) return;
    setBusy(f.id);
    try {
      await api.post("/tasks", {
        assignee_id, voter_id: f.id, voter_name: f.company_name,
        note: `${f.segment_title}: ${f.why} → ${f.action}`,
      });
      toast.success("Görev oluşturuldu");
      setAssignee((m) => ({ ...m, [f.id]: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Görev oluşturulamadı");
    } finally { setBusy(null); }
  };

  const s = rep?.summary;

  return (
    <div className="space-y-5" data-testid="strategy-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Lightbulb className="h-5 w-5 text-amber-500" />
        <div>
          <h2 className="text-lg font-bold text-[#0F2C59] dark:text-sky-300">Strateji Danışmanı</h2>
          <p className="text-xs text-muted-foreground">Komitenin mevcut halini yorumlar, riskleri çıkarır, firma bazında ne yapılacağını söyler</p>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {branches.length > 0 && (
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="h-9 w-[170px]" data-testid="strategy-branch"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Tüm şubeler (merkez dâhil)</SelectItem>
                {branches.map((b) => <SelectItem key={b} value={b}>{b} şubesi</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={committee} onValueChange={setCommittee}>
            <SelectTrigger className="h-9 w-[230px]" data-testid="strategy-committee"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tüm komitelerim</SelectItem>
              {committees.map((c) => <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} · {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="strategy-refresh"><RefreshCw className="h-4 w-4 mr-1.5" />Yenile</Button>
          <Button variant="outline" size="sm" className="h-9" disabled={!rep || pdfBusy} onClick={exportPdf} data-testid="strategy-export-pdf">
            <FileText className="h-4 w-4 mr-1.5" />{pdfBusy ? "Hazırlanıyor..." : "PDF"}
          </Button>
        </div>
      </div>

      {!rep ? <Skeleton className="h-64 w-full" /> : (
        <>
          {rep.wins?.length > 0 && (
            <Card className="p-4 border border-emerald-500/40 bg-emerald-500/5" data-testid="strategy-wins">
              <div className="flex items-center gap-2">
                <ThumbsUp className="h-4 w-4 text-emerald-600" />
                <p className="text-sm font-semibold">İyi Gidenler</p>
                <span className="text-xs text-muted-foreground ml-auto">Başkan özeti</span>
              </div>
              <ol className="mt-2 space-y-1.5">
                {rep.wins.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`strategy-win-${i}`}>
                    <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-emerald-600 text-white text-[11px] font-bold grid place-items-center">{i + 1}</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ol>
            </Card>
          )}
          <Card className={`p-4 border ${s.behind ? "border-red-500/50 bg-red-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`} data-testid="strategy-narrative">
            <div className="flex items-center gap-2">
              <Target className={`h-4 w-4 ${s.behind ? "text-red-600" : "text-emerald-600"}`} />
              <p className="text-sm font-semibold">Durum Değerlendirmesi</p>
              <span className="text-xs font-medium text-muted-foreground">{rep.scope}</span>
              <span className="text-xs text-muted-foreground ml-auto">Seçime {s.days_left} gün</span>
            </div>
            <p className="mt-2 text-sm leading-relaxed">{rep.narrative}</p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              {[
                { l: "Güvenceli oy", v: `${s.secured}/${s.vote_target}` },
                { l: "Yetki belgesi", v: `${s.belge}/${s.belge_goal}` },
                { l: "Günde gereken", v: `${s.need_votes_per_day} oy · ${s.need_belge_per_day} belge` },
                { l: "Son 7 gün hızı", v: `${s.pace_votes} oy · ${s.pace_belge} belge` },
              ].map((k) => (
                <div key={k.l} className="rounded-lg bg-background/60 border border-border p-2">
                  <p className="text-sm font-bold">{k.v}</p>
                  <p className="text-[11px] text-muted-foreground">{k.l}</p>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4 space-y-2" data-testid="strategy-risks">
              <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" /><p className="text-sm font-semibold">Kritik Riskler</p></div>
              {rep.risks.length === 0 ? <p className="text-sm text-muted-foreground py-3">Belirgin bir risk görünmüyor.</p> : rep.risks.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-sm rounded-md border border-border p-2.5" data-testid={`strategy-risk-${i}`}>
                  <span className={`mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white shrink-0 ${LEVEL[r.level]?.cls}`}>{LEVEL[r.level]?.l}</span>
                  <span>{r.text}</span>
                </div>
              ))}
            </Card>

            <Card className="p-4 space-y-2" data-testid="strategy-plan">
              <div className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-[#0F2C59] dark:text-sky-300" /><p className="text-sm font-semibold">Bu Hafta Yapılacaklar</p></div>
              {rep.plan.length === 0 ? <p className="text-sm text-muted-foreground py-3">Acil bir iş görünmüyor, teyit turlarına devam edin.</p> : rep.plan.map((p, i) => (
                <div key={i} className="rounded-md border border-border p-2.5" data-testid={`strategy-plan-${i}`}>
                  <p className="text-sm font-medium">{i + 1}. {p.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.detail}</p>
                  <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${p.impact === "Yüksek" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>Etki: {p.impact}</span>
                </div>
              ))}
            </Card>
          </div>

          <Card className="p-4 space-y-3" data-testid="strategy-firms">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="h-4 w-4 text-cyan-600" />
              <p className="text-sm font-semibold">Firma Bazında Öneriler</p>
              <div className="flex items-center gap-1.5 flex-wrap ml-auto">
                <button type="button" onClick={() => setSeg("all")} data-testid="strategy-seg-all"
                  className={`px-2.5 py-1 text-xs font-medium rounded-md border ${seg === "all" ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-border text-muted-foreground"}`}>
                  Tümü ({rep.firms.length})
                </button>
                {rep.segments.filter((x) => x.count > 0).map((x) => (
                  <button key={x.key} type="button" onClick={() => setSeg(x.key)} data-testid={`strategy-seg-${x.key}`}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md border ${seg === x.key ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-border text-muted-foreground"}`}>
                    {x.title} ({x.count})
                  </button>
                ))}
              </div>
            </div>
            {seg !== "all" && (
              <p className="text-xs text-muted-foreground" data-testid="strategy-seg-desc">
                {rep.segments.find((x) => x.key === seg)?.desc}
              </p>
            )}
            {firms.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Bu grupta öneri yok.</p> : (
              <div className="overflow-x-auto max-h-[460px] overflow-y-auto rounded-md border border-border">
                <table className="w-full text-sm" data-testid="strategy-firms-table">
                  <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <stT.Th sortKey="st-firma" get={(r) => r.company_name} text className="px-3 py-2 font-medium">Firma</stT.Th>
                      <stT.Th sortKey="st-neden" get={(r) => r.why} text className="px-3 py-2 font-medium">Neden</stT.Th>
                      <stT.Th sortKey="st-yapilmali" get={(r) => r.action} text className="px-3 py-2 font-medium">Ne yapılmalı</stT.Th>
                      <stT.Th sortKey="st-sorumlu" get={(r) => r.owner} text className="px-3 py-2 font-medium">Sorumlu</stT.Th>
                      <th className="px-3 py-2 font-medium">Görev</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stT.applySort(firms).map((f) => (
                      <tr key={`${f.segment}-${f.id}`} className="border-t border-border/60" data-testid={`strategy-firm-${f.id}`}>
                        <td className="px-3 py-2">
                          <p className="font-medium">{f.company_name}</p>
                          <p className="text-[11px] text-muted-foreground">#{f.committee_no} · {f.segment_title}</p>
                        </td>
                        <td className="px-3 py-2 text-xs">{f.why}</td>
                        <td className="px-3 py-2 text-xs">{f.action}</td>
                        <td className="px-3 py-2 text-xs">{f.owner}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <Select value={assignee[f.id] || ""} onValueChange={(v) => setAssignee((m) => ({ ...m, [f.id]: v }))}>
                              <SelectTrigger className="h-8 w-[140px]" data-testid={`strategy-assignee-${f.id}`}><SelectValue placeholder="Kişi seç" /></SelectTrigger>
                              <SelectContent className="max-h-72">
                                {dirUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="outline" className="h-8" disabled={!assignee[f.id] || busy === f.id} onClick={() => assign(f)} data-testid={`strategy-assign-${f.id}`}>Ata</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
